import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Add, Delete, Edit, Search } from '@mui/icons-material';
import { precoCombustivelService, PrecoCombustivel } from '../services/precoCombustivelService';
import { postoService } from '../services/postoService';
import { PostoCombustivel } from '../types';
import { useAuth } from '../contexts/AuthContext';

type TipoCombustivel = 'GASOLINA' | 'ETANOL' | 'DIESEL' | 'GNV' | 'FLEX' | 'DIESEL_S10' | 'DIESEL_COMUM';

const tiposCombustivel: TipoCombustivel[] = ['GASOLINA', 'ETANOL', 'DIESEL', 'FLEX', 'GNV', 'DIESEL_S10', 'DIESEL_COMUM'];

const PrecosCombustivel: React.FC = () => {
  const { user } = useAuth();
  const [precos, setPrecos] = useState<PrecoCombustivel[]>([]);
  const [postos, setPostos] = useState<PostoCombustivel[]>([]);
  const [filtro, setFiltro] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PrecoCombustivel | null>(null);
  const [form, setForm] = useState<PrecoCombustivel>({
    postoCombustivel: '',
    tipoCombustivel: 'GASOLINA',
    valorLitro: 0,
    data: '',
    documentoValidacao: '',
    numeroDocumento: '',
    quantidadeLitrosContratados: 0,
    observacao: '',
  });

  const isGestor = user?.perfil === 'ADMIN' || user?.perfil === 'GERENTE' || user?.perfil === 'SUPER_ADMIN';

  const carregar = async () => {
    try {
      const [res, postosData] = await Promise.all([
        precoCombustivelService.listar(),
        postoService.listarAtivos(),
      ]);
      setPrecos(res.data);
      setPostos(postosData);
    } catch (error) {
      console.error('Erro ao carregar preços', error);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const handleOpen = (preco?: PrecoCombustivel) => {
    if (preco) {
      setEditing(preco);
      setForm({ ...preco, data: preco.data || '' });
    } else {
      setEditing(null);
      setForm({ postoCombustivel: '', tipoCombustivel: 'GASOLINA', valorLitro: 0, data: '', documentoValidacao: '', numeroDocumento: '', quantidadeLitrosContratados: 0, observacao: '' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleSalvar = async () => {
    try {
      if (editing?.id) {
        await precoCombustivelService.atualizar(editing.id, form);
      } else {
        await precoCombustivelService.cadastrar(form);
      }
      handleClose();
      carregar();
    } catch (error) {
      console.error('Erro ao salvar preço', error);
    }
  };

  const handleExcluir = async (id: number) => {
    if (window.confirm('Deseja excluir este preço?')) {
      try {
        await precoCombustivelService.excluir(id);
        carregar();
      } catch (error) {
        console.error('Erro ao excluir', error);
      }
    }
  };

  const termo = filtro.trim().toLowerCase();
  const precosFiltrados = termo
    ? precos.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : precos;

  return (
    <Container disableGutters maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Preços de Combustível</Typography>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          {isGestor && (
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
              Novo Preço
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Posto</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Valor por Litro (R$)</TableCell>
                  <TableCell>Documento</TableCell>
                  <TableCell>Nº Documento</TableCell>
                  <TableCell>Litros Contratados</TableCell>
                  <TableCell>Observação</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {precosFiltrados.map((preco) => (
                  <TableRow key={preco.id}>
                    <TableCell>{preco.data ? new Date(preco.data + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</TableCell>
                    <TableCell>{preco.postoCombustivel || '-'}</TableCell>
                    <TableCell>{preco.tipoCombustivel}</TableCell>
                    <TableCell>R$ {Number(preco.valorLitro).toFixed(2)}</TableCell>
                    <TableCell>{preco.documentoValidacao || '-'}</TableCell>
                    <TableCell>{preco.numeroDocumento || '-'}</TableCell>
                    <TableCell>{preco.quantidadeLitrosContratados ? Number(preco.quantidadeLitrosContratados).toLocaleString('pt-BR') : '-'}</TableCell>
                    <TableCell>{preco.observacao || '-'}</TableCell>
                    <TableCell align="right">
                      {isGestor && (
                        <>
                          <IconButton onClick={() => handleOpen(preco)}>
                            <Edit />
                          </IconButton>
                          <IconButton onClick={() => preco.id && handleExcluir(preco.id)}>
                            <Delete />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {precosFiltrados.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Nenhum preço cadastrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar Preço' : 'Novo Preço'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.data || ''}
                onChange={(e) => setForm({ ...form, data: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Posto de Combustível</InputLabel>
                <Select
                  value={form.postoCombustivel || ''}
                  label="Posto de Combustível"
                  onChange={(e) => setForm({ ...form, postoCombustivel: e.target.value })}
                >
                  <MenuItem value="">Todos / Não informado</MenuItem>
                  {postos.map((posto) => (
                    <MenuItem key={posto.id} value={posto.nome}>
                      {posto.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Combustível</InputLabel>
                <Select
                  value={form.tipoCombustivel}
                  label="Tipo de Combustível"
                  onChange={(e) => setForm({ ...form, tipoCombustivel: e.target.value as TipoCombustivel })}
                >
                  {tiposCombustivel.map((tipo) => (
                    <MenuItem key={tipo} value={tipo}>
                      {tipo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valor por Litro (R$)"
                type="number"
                inputProps={{ step: '0.01' }}
                value={form.valorLitro}
                onChange={(e) => setForm({ ...form, valorLitro: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Documento que validou</InputLabel>
                <Select
                  value={form.documentoValidacao || ''}
                  label="Documento que validou"
                  onChange={(e) => setForm({ ...form, documentoValidacao: e.target.value })}
                >
                  <MenuItem value="CONTRATO">CONTRATO</MenuItem>
                  <MenuItem value="ARP">ARP</MenuItem>
                  <MenuItem value="ADITIVO">ADITIVO</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nº do Documento"
                value={form.numeroDocumento || ''}
                onChange={(e) => setForm({ ...form, numeroDocumento: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantidade de litros contratados"
                type="number"
                inputProps={{ step: '0.001' }}
                value={form.quantidadeLitrosContratados || 0}
                onChange={(e) => setForm({ ...form, quantidadeLitrosContratados: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observação"
                value={form.observacao || ''}
                onChange={(e) => setForm({ ...form, observacao: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSalvar} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PrecosCombustivel;
